import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../Accounts/AuthContext';
import { ctaJump, ctaTarget, isMarketingRoute, marketingAudience, activeMarketingPath, MARKETING_CTA, AUDIENCE_LINKS } from './marketingRoutes';
import { jumpClick } from './jumpTo';
import BrandLockup from '../components/brand/BrandLockup';
import {
  User,
  LogOut,
  ClipboardList,
  Mail,
  GraduationCap,
  // Handshake,
  Home,        // Home
  Search,      // Diagnostic (magnifying glass)
  Star,        // NEW reading flow
  Menu,        // mobile hamburger
  School,      // Schools (mobile marketing nav)
  Users,       // Teachers (mobile marketing nav)
  FlaskConical, // QA content browser (Teacher/Admin)
  CheckCircle2, // QA lesson review (Teacher/Admin)
} from 'lucide-react';

const NavigationBar = () => {
  const { isLoggedIn, user, logout, userData } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();

  const isInPreviewMode = location.pathname.startsWith('/teacher/student-preview/');
  // Content Workbench domain audit (/session/*?audit=1): show the STUDENT nav
  // regardless of who is signed in, so the page reads exactly as a student sees it.
  const isAuditPreview = location.pathname.startsWith('/session/')
    && new URLSearchParams(location.search).get('audit') === '1';
  const effectiveUserRole = isInPreviewMode || isAuditPreview ? 'Student' : user?.role;

  // Marketing surfaces (Sandy 2026-07-27) show the marketing links and one
  // audience-matched CTA — "Book a demo" is a district action and reads wrong
  // next to student or single-teacher copy. Signed in, the CTA pair collapses
  // to a single way back into the product.
  const onMarketing = isMarketingRoute(location.pathname);
  const baseCta = MARKETING_CTA[marketingAudience(location.pathname)];
  // The schools preview (localhost only) swaps every CTA below for an
  // in-page "Get in touch" jump; everywhere else jump is null (no change).
  const jump = ctaJump(location.pathname);
  const jumpTarget = jump ? { label: jump.label, to: `#${jump.hash}` } : null;
  const cta = jumpTarget || { ...baseCta, to: ctaTarget(location.pathname, baseCta.to) };
  // Phone-bar CTA (Sandy 2026-08-18, Notion pattern): students sign up
  // (rainbow outline), schools ask for a demo (black). Teachers get the same
  // free-class signup as the desktop nav (Sandy 2026-09-29: no school pitch
  // on the teacher pages; one label everywhere). Students go straight to
  // the signup page, nothing in between (Sandy 2026-10-12).
  const mobileNavCta = jumpTarget || {
    student: { label: 'Sign up now', to: '/signup', rainbow: true },
    teacher: { label: 'Set up my class', to: ctaTarget(location.pathname, '/teacherprogram/signup') },
    school: { label: 'Request a demo', to: '/contact?mode=school' },
    signin: { label: 'Create an account', to: '/signup' },
  }[marketingAudience(location.pathname)];
  // undefined off the schools preview, so those Links render exactly as before
  const onCtaJump = jump ? jumpClick(jump.hash) : undefined;
  // Marketing chrome for logged-out visitors anywhere, and for everyone on a
  // marketing route.
  const showMarketingLinks = (!isLoggedIn && !isAuditPreview) || onMarketing;

  // Marketing nav (Sandy 2026-09-30): a full-width bar fixed across the
  // top. Scrolling only turns it solid (hairline under it) and collapses
  // the brand lockup to the pixel head; the bar never shrinks or moves.
  // App chrome (signed in, non-marketing) keeps the classic bar.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const floating = showMarketingLinks;
  const compact = floating && scrolled;

  // Dark-section awareness (Sandy 2026-08-03, per the Amplemarket reference):
  // sections that are dark mark themselves with data-nav-dark; when one
  // passes under the top strip, the island, blur layer and buttons flip to
  // the dark glass variant instead of fogging the black with white blur.
  const [navDark, setNavDark] = useState(false);
  useEffect(() => {
    const check = () => {
      let dark = false;
      document.querySelectorAll('[data-nav-dark]').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < 90 && r.bottom > 0) dark = true;
      });
      setNavDark(dark);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, [location.pathname]);
  const dark = floating && navDark;

  // // Function to check if user is a student with "No School"
  // const isStudentWithNoSchool = () => {
  //   if (!user || user.role !== 'Student') return false;

  //   if (userData && Array.isArray(userData) && userData.length > 0) {
  //     return userData[0].School && userData[0].School.S === "No School";
  //   }

  //   return false;
  // };

  // Function to check if a route is active
  const isActiveRoute = (path) => {
    return activeMarketingPath(location.pathname) === path;
  };

  // Helper function to get dashboard route based on role (V3 dashboards are
  // the defaults; the old /teacher-dashboard and /student-dashboard routes
  // stay reachable directly but are no longer linked as home).
  const getDashboardRoute = () => {
    if (effectiveUserRole === 'Admin') {
      return '/admin-dashboard';
    } else if (effectiveUserRole === 'Teacher') {
      return '/teacher-dashboard';
    }
    return '/student-dashboard';
  };

  // Helper function to check if current route matches dashboard for the role
  const isDashboardActive = () => {
    if (effectiveUserRole === 'Admin') {
      return location.pathname.startsWith('/admin-dashboard');
    } else if (effectiveUserRole === 'Teacher') {
      return isActiveRoute('/teacher-dashboard');
    }
    return isActiveRoute('/student-dashboard');
  };

  // Event handlers for the dropdown menu
  const handleMouseEnter = (itemId) => {
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const dropdownStyle = {
    textDecoration: 'none',
    display: 'block',
    textAlign: 'left',
    fontFamily: 'inherit', // follows the <nav> face
    fontSize: '14px',
    fontWeight: 600,
    color: '#141414', // ink
    backgroundColor: '#ffffff',
    borderRadius: '8px',
  };

  const hoverStyle = {
    ...dropdownStyle,
    backgroundColor: '#F2F2F2', // neutral-100 hover wash
  };

  const handleSignOut = async () => {
    try {
      logout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Navigation button, Brilliant-style: no boxes. Inactive = gray, hover
  // darkens; active = ink text with the moving rainbow underline bar
  // (.nav-active-rainbow in tailwind.css - the hero frame's gradient).
  // The desktop marketing links carry no leading icons. Their hover
  // preview panels were removed (Sandy 2026-10-12): plain links.
  const NavButton = ({ to, children, icon: Icon, isActive = false }) => (
    <Link
      to={to}
      style={{ textDecoration: 'none' }}
      className="self-center flex"
    >
      <button
        className={`cursor-pointer ${showMarketingLinks ? 'h-10' : 'h-9'} px-4 flex flex-row items-center justify-center gap-2 bg-transparent transition-colors ${isActive
          ? (showMarketingLinks
            ? (dark ? 'text-white nav-active-soft-dark rounded-[8px]' : 'text-ink nav-active-soft rounded-[8px]')
            : 'text-ink relative nav-active-rainbow')
          : (dark ? 'text-white/60 hover:text-white' : 'text-neutral-500 hover:text-ink')
          }`}
      >
        {Icon && <Icon className={`w-4 h-4 flex-none transition-colors ${isActive ? (dark ? 'text-white' : 'text-ink') : (dark ? 'text-white/50' : 'text-neutral-400')}`} strokeWidth={2} />}
        {/* Marketing links: DM Sans at MEDIUM, not semibold - the thinner
            cut Sandy asked for (2026-08-04). App chrome keeps semibold. */}
        <span className={`${showMarketingLinks ? 'text-[15px] font-medium' : 'text-[14px] font-semibold'} leading-none whitespace-nowrap`}>
          {children}
        </span>
      </button>
    </Link>
  );

  // Helper function for mobile nav buttons
  const MobileNavButton = ({ to, children, icon: Icon, onClick, isActive = false }) => (
    <Link to={to} style={{ textDecoration: 'none' }} onClick={onClick}>
      <button
        className={`cursor-pointer py-2.5 px-4 rounded-[12px] flex flex-row items-center justify-start gap-3 w-full transition-colors ${isActive
          ? 'bg-neutral-100 text-primary-600'
          : 'bg-transparent text-neutral-700 hover:bg-neutral-100 hover:text-ink'
          }`}
      >
        {Icon && <Icon className={`w-4 h-4 flex-none transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-500'}`} strokeWidth={2} />}
        <span className="text-[14px] font-semibold leading-none text-left">
          {children}
        </span>
      </button>
    </Link>
  );

  return (
    // Hairline + a whisper of shading fading into the page below (Brilliant's header treatment)
    // font-sans is DM Sans app-wide now; the buttons and dropdown below
    // inherit it rather than naming a face of their own. On marketing routes
    // the header also opts out of the app's x-height compensation so it is on
    // the same type scale as the page beneath it.
    <>
    <nav
      className={
        'font-sans pointer-events-auto ' +
        (floating
          /* Marketing bar (Sandy 2026-09-30: no more shrinking island): a
             full-width bar fixed across the top, the same 64 px in both
             states, so nothing moves on scroll. At rest it is chromeless
             (the page shows through); once scrolled it turns solid with a
             1 px hairline under it (box-shadow, not `border`: Bootstrap's
             .border is !important with its own gray). Only the background
             color fades; the brand lockup collapses to the head. */
          ? 'marketing-type relative w-full transition-colors duration-200 ' +
            (scrolled
              ? (dark
                ? 'bg-[#1a1a1a] shadow-[0_1px_0_rgba(255,255,255,0.10)]'
                : 'bg-white shadow-[0_1px_0_#E5E5E5]')
              : 'bg-transparent')
          : 'bg-white border-b border-neutral-200 shadow-[0_6px_16px_-12px_rgba(20,20,20,0.15)]')
      }
      style={{ zIndex: 9999 }}
    >
      {/* Phones hug the edges hard (px-3 vs desktop px-6) - Sandy 2026-08-18:
          logo and controls sit ~12px off the screen edges. */}
      <div className={'lg:flex lg:justify-between lg:items-center' + (floating ? ' px-3 lg:px-6 py-3' : '')}>
        {/* Logo and Hamburger button */}
        <div className="flex items-center">

          {/* h-10: the row stays button height (the bar stays 64 px); the
              42-45 px lockup overflows it evenly, centered on the buttons */}
          <Link to="/" style={{ textDecoration: 'none' }} className={floating ? 'flex items-center h-10' : 'flex items-center'}>
            {floating ? (
              /* Brand lockup (Sandy 2026-09-30, exports/brain-buffs-logo.html):
                 pixel head | Brain Buffs at the top of the page; scrolling
                 down slides the words behind the divider, leaving only the
                 head, and scrolling back up brings them back. Same on phones.
                 The lockup keeps its width while collapsed, so the links and
                 CTA never shift. */
              <BrandLockup collapsed={compact} tone={dark ? 'dark' : 'light'} className="bb-lockup--narrow-head" />
            ) : (
              <div className="flex items-center px-4 py-2">
                {/* App chrome (signed in, non-marketing) keeps the blue logo. */}
                <img className="block w-48 h-auto" src="/BrainBuffsLogo-math.png" alt="Brain Buffs" />
              </div>
            )}
          </Link>


          {/* Phone nav CTA (Sandy 2026-08-18, Notion pattern): one audience-
              matched button riding just left of the hamburger. Students get
              the rainbow-outline signup; teachers and schools get the black
              demo ask. Signed-in visitors keep the uncluttered bar. */}
          {showMarketingLinks && !isLoggedIn && (
            <Link
              to={mobileNavCta.to}
              onClick={onCtaJump}
              className={`lg:hidden ml-auto inline-flex shrink-0 whitespace-nowrap items-center justify-center h-9 px-4 rounded-[10px] text-[14px] font-semibold no-underline transition-colors ${
                mobileNavCta.rainbow
                  ? 'rainbow-outline text-ink'
                  : 'bg-ink hover:bg-neutral-900 active:bg-neutral-800 text-white'
              }`}
            >
              {mobileNavCta.label}
            </Link>
          )}

          {/* Hamburger icon for md and smaller screens */}
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden ${showMarketingLinks && !isLoggedIn ? 'ml-2' : 'ml-auto'} ${floating ? 'mr-0' : 'mr-3'} bg-white p-2 rounded-[6px] text-neutral-700 hover:bg-neutral-100 transition-colors`}
          >
            <Menu className="block w-5 h-5" strokeWidth={2} />
          </button>
        </div>

        {/* Navigation links for larger screens - CENTERED.
            Marketing nav on the public pages (and for anyone logged out); the
            app nav (Dashboard/NEW/Diagnostic) everywhere behind login. A
            signed-in visitor reading a marketing page gets the marketing links
            plus "Go to dashboard" on the right as the way back. */}
        <div className="hidden lg:flex items-center justify-center gap-2 flex-1">
          {showMarketingLinks ? (
            <>
              {/* The ROOT is the STUDENT pitch now (Sandy 2026-08-12); the
                  schools pitch lives at /schools. Plain nouns, not "For X"
                  (Sandy 2026-08-04). TODO(sandy): point Teachers at the real
                  for-teachers page once it exists (teacherprogram page retired). */}
              <NavButton
                to={AUDIENCE_LINKS.schools}
                isActive={isActiveRoute('/for-schools')}
              >
                Schools
              </NavButton>
              <NavButton
                to={AUDIENCE_LINKS.teachers}
                isActive={isActiveRoute('/teachers')}
              >
                Teachers
              </NavButton>
              <NavButton
                to={AUDIENCE_LINKS.students}
                isActive={isActiveRoute('/students') || isActiveRoute('/')}
              >
                Students
              </NavButton>
            </>
          ) : effectiveUserRole === 'Teacher' || effectiveUserRole === 'Admin' ? (
            <>
              <NavButton
                to={getDashboardRoute()}
                icon={ClipboardList}
                isActive={isDashboardActive()}
              >
                Dashboard
              </NavButton>
              <NavButton
                to="/qa"
                icon={FlaskConical}
                isActive={isActiveRoute('/qa')}
              >
                QA Preview
              </NavButton>
              <NavButton
                to="/qa/lessons"
                icon={CheckCircle2}
                isActive={isActiveRoute('/qa/lessons')}
              >
                Lesson Review
              </NavButton>
              <NavButton
                to="/contact"
                icon={Mail}
                isActive={isActiveRoute('/contact')}
              >
                Contact
              </NavButton>
            </>
          ) : (
            <>
              <NavButton
                to={isInPreviewMode ? '/teacher/student-preview/dashboard' : `/student-dashboard?reset=${Date.now()}`}
                icon={Home}
                isActive={isInPreviewMode ? isActiveRoute('/teacher/student-preview/dashboard') : isActiveRoute('/student-dashboard')}
              >
                Dashboard
              </NavButton>
              <NavButton
                to="/student-dashboard"
                icon={Star}
                isActive={location.pathname.startsWith('/student-dashboard') || location.pathname.startsWith('/session/') || location.pathname.startsWith('/domain/')}
              >
                NEW
              </NavButton>
              <NavButton
                to={isInPreviewMode ? '/teacher/student-preview/diagnostic' : '/diagnostic'}
                icon={Search}
                isActive={isInPreviewMode ? isActiveRoute('/teacher/student-preview/diagnostic') : isActiveRoute('/diagnostic')}
              >
                Diagnostic
              </NavButton>
              <div
                onClick={isInPreviewMode ? (e) => { e.preventDefault(); alert('Contact is disabled in preview mode'); } : undefined}
                style={isInPreviewMode ? { cursor: 'not-allowed' } : {}}
                className="self-center flex"
              >
                <NavButton
                  to={isInPreviewMode ? '#' : '/contact'}
                  icon={Mail}
                  isActive={isActiveRoute('/contact')}
                >
                  Contact
                </NavButton>
              </div>
              {/* {isStudentWithNoSchool() && (
                <NavButton
                  to="/AffiliateDashboard"
                  icon={Handshake}
                  isActive={isActiveRoute('/AffiliateDashboard')}
                >
                  Affiliates
                </NavButton>
              )} */}
            </>
          )}
        </div>

        {/* Right side, desktop: logged out = "Log in" text + the audience CTA;
            logged in on a marketing page = "Go to dashboard"; then the account
            dropdown in every signed-in case. */}
        <div
          className={'relative hidden lg:flex items-center ' + (floating ? 'gap-2.5' : 'gap-5 mr-4')}
          onMouseLeave={() => setIsOpen(false)}
        >
          {!isLoggedIn && !isAuditPreview && (
            <>
              {/* Boxed like the reference's "Open app": quiet outlined box
                  tight beside the solid CTA. */}
              <Link to="/signin" style={{ textDecoration: 'none' }}>
                <button className={'cursor-pointer h-10 px-4 rounded-[8px] text-[15px] font-semibold whitespace-nowrap transition-colors ' + (dark
                  ? 'bg-white/10 border border-white/25 hover:bg-white/20 text-white'
                  : 'bg-white/70 border border-neutral-300 hover:bg-white text-ink')}>
                  Log in
                </button>
              </Link>
              <Link to={cta.to} style={{ textDecoration: 'none' }} onClick={onCtaJump}>
                <button className={'cursor-pointer h-10 px-4 rounded-[8px] text-[15px] font-semibold whitespace-nowrap transition-colors ' + (dark
                  ? 'bg-white hover:bg-neutral-200 text-ink'
                  : 'bg-ink hover:bg-neutral-900 text-white')}>
                  {cta.label}
                </button>
              </Link>
            </>
          )}
          {isLoggedIn && onMarketing && (
            <Link to={getDashboardRoute()} style={{ textDecoration: 'none' }}>
              <button className="cursor-pointer h-10 px-4 rounded-[8px] bg-ink hover:bg-neutral-900 text-white text-[15px] font-semibold whitespace-nowrap">
                Go to dashboard
              </button>
            </Link>
          )}
          {isLoggedIn && (
          <div
            className="flex items-center gap-2 cursor-pointer transition-colors h-9 px-3 rounded-[12px] text-ink hover:bg-neutral-100"
            onMouseEnter={() => setIsOpen(true)}
          >
            <User className="w-4 h-4 flex-none text-neutral-500" strokeWidth={2} />
            <div
              className="text-[14px] font-semibold leading-none"
              style={{ whiteSpace: 'nowrap' }}
            >
              {user.Name || user.email}
            </div>
          </div>
          )}
          {isLoggedIn && isOpen && (
            <div
              className="absolute right-0 top-full bg-white border border-neutral-200 rounded-[12px] shadow-lg justify-end min-w-[160px]"
              onMouseLeave={() => setIsOpen(false)}
              onClick={() => setIsOpen(false)}
            >
              <ul className="list-none p-1 m-0">
                {isLoggedIn ? (
                  <>
                    <li>
                      <Link
                        to={isInPreviewMode ? '#' : '/manageAccount'}
                        onMouseEnter={() => handleMouseEnter('acc')}
                        onMouseLeave={handleMouseLeave}
                        onClick={isInPreviewMode ? (e) => { e.preventDefault(); alert('Account is disabled in preview mode'); } : undefined}
                        style={{
                          ...dropdownStyle,
                          padding: '8px 12px',
                          ...(hoveredItem === 'acc' ? hoverStyle : {}),
                          display: 'flex',
                          alignItems: 'center',
                          ...(isInPreviewMode ? { cursor: 'not-allowed' } : {})
                        }}
                      >
                        <User className="w-4 h-4 flex-none mr-2 text-neutral-500" strokeWidth={2} />
                        Account
                      </Link>
                    </li>
                    <li>
                      <Link
                        onClick={handleSignOut}
                        onMouseEnter={() => handleMouseEnter('so')}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          ...dropdownStyle,
                          border: 'none',
                          padding: '8px 12px',
                          color: '#dc2626',
                          ...(hoveredItem === 'so' ? { ...hoverStyle, color: '#dc2626' } : {}),
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <LogOut className="w-4 h-4 flex-none mr-2" strokeWidth={2} />
                        Sign Out
                      </Link>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <Link
                        to="/signin"
                        onMouseEnter={() => handleMouseEnter('si')}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          ...dropdownStyle,
                          padding: '8px 12px',
                          ...(hoveredItem === 'si' ? hoverStyle : {})
                        }}
                      >
                        Log In
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/signup"
                        onMouseEnter={() => handleMouseEnter('su')}
                        onMouseLeave={handleMouseLeave}
                        style={{
                          ...dropdownStyle,
                          padding: '8px 12px',
                          ...(hoveredItem === 'su' ? hoverStyle : {})
                        }}
                      >
                        Sign Up
                      </Link>
                    </li>
                  </>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Mobile navigation links */}
        <div className={`${isMenuOpen ? 'block' : 'hidden'} lg:hidden bg-white border-t border-neutral-200 px-2 py-2`}>
          {showMarketingLinks ? (
            <>
              <MobileNavButton
                to={AUDIENCE_LINKS.schools}
                icon={School}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isActiveRoute('/for-schools')}
              >
                Schools
              </MobileNavButton>
              <MobileNavButton
                to={AUDIENCE_LINKS.teachers}
                icon={Users}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isActiveRoute('/teachers')}
              >
                Teachers
              </MobileNavButton>
              <MobileNavButton
                to={AUDIENCE_LINKS.students}
                icon={GraduationCap}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isActiveRoute('/students') || isActiveRoute('/')}
              >
                Students
              </MobileNavButton>
            </>
          ) : effectiveUserRole === 'Teacher' || effectiveUserRole === 'Admin' ? (
            <>
              <MobileNavButton
                to={getDashboardRoute()}
                icon={ClipboardList}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isDashboardActive()}
              >
                Dashboard
              </MobileNavButton>
              <MobileNavButton
                to="/contact"
                icon={Mail}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isActiveRoute('/contact')}
              >
                Contact
              </MobileNavButton>
            </>
          ) : (
            <>
              <MobileNavButton
                to={isInPreviewMode ? '/teacher/student-preview/dashboard' : `/student-dashboard?reset=${Date.now()}`}
                icon={Home}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isInPreviewMode ? isActiveRoute('/teacher/student-preview/dashboard') : isActiveRoute('/student-dashboard')}
              >
                Dashboard
              </MobileNavButton>
              <MobileNavButton
                to="/student-dashboard"
                icon={Star}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={location.pathname.startsWith('/student-dashboard') || location.pathname.startsWith('/session/') || location.pathname.startsWith('/domain/')}
              >
                NEW
              </MobileNavButton>
              <MobileNavButton
                to={isInPreviewMode ? '/teacher/student-preview/diagnostic' : '/diagnostic'}
                icon={Search}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isInPreviewMode ? isActiveRoute('/teacher/student-preview/diagnostic') : isActiveRoute('/diagnostic')}
              >
                Diagnostic
              </MobileNavButton>
              <div
                onClick={isInPreviewMode ? (e) => { e.preventDefault(); alert('Contact is disabled in preview mode'); } : () => setIsMenuOpen(!isMenuOpen)}
                style={isInPreviewMode ? { cursor: 'not-allowed' } : {}}
              >
                <MobileNavButton
                  to={isInPreviewMode ? '#' : '/contact'}
                  icon={Mail}
                  isActive={isActiveRoute('/contact')}
                >
                  Contact
                </MobileNavButton>
              </div>
              {/* {isStudentWithNoSchool() && (
                <MobileNavButton
                  to="/AffiliateDashboard"
                  icon={Handshake}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  isActive={isActiveRoute('/AffiliateDashboard')}
                >
                  Affiliates
                </MobileNavButton>
              )} */}
            </>
          )}

          {/* Mobile user section */}
          {isLoggedIn ? (
            <div className="border-t border-neutral-200 mt-2 pt-2">
              {onMarketing && (
                <Link to={getDashboardRoute()} style={{ textDecoration: 'none' }} onClick={() => setIsMenuOpen(false)}>
                  <button className="cursor-pointer h-11 w-full mb-2 px-4 rounded-[12px] bg-ink hover:bg-neutral-900 text-white text-[14px] font-semibold">
                    Go to dashboard
                  </button>
                </Link>
              )}
              <div
                onClick={isInPreviewMode ? (e) => { e.preventDefault(); alert('Account is disabled in preview mode'); } : () => setIsMenuOpen(!isMenuOpen)}
                style={isInPreviewMode ? { cursor: 'not-allowed' } : {}}
              >
                <MobileNavButton
                  to={isInPreviewMode ? '#' : '/manageAccount'}
                  icon={User}
                  isActive={isActiveRoute('/manageAccount')}
                >
                  Account
                </MobileNavButton>
              </div>
              {/* <MobileNavButton
                to="/friends"
                icon={Users}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isActiveRoute('/friends')}
              >
                Friends
              </MobileNavButton> */}
              <Link onClick={handleSignOut} style={{ textDecoration: 'none' }}>
                <button className="cursor-pointer py-2.5 px-4 bg-transparent rounded-[12px] flex flex-row items-center justify-start gap-3 w-full text-red-600 hover:bg-red-50 transition-colors">
                  <LogOut className="w-4 h-4 flex-none" strokeWidth={2} />
                  <span className="text-[14px] font-semibold leading-none text-left">
                    Sign Out
                  </span>
                </button>
              </Link>
            </div>
          ) : (
            <div className="border-t border-neutral-200 mt-2 pt-2">
              {/* Same one-action-per-audience rule as desktop; signing in is
                  the quieter second option below it. */}
              <Link to={cta.to} style={{ textDecoration: 'none' }} onClick={jump ? jumpClick(jump.hash, () => setIsMenuOpen(false)) : () => setIsMenuOpen(false)}>
                <button className="cursor-pointer h-11 w-full mb-2 px-4 rounded-[12px] bg-ink hover:bg-neutral-900 text-white text-[14px] font-semibold">
                  {cta.label}
                </button>
              </Link>
              <MobileNavButton
                to="/signin"
                icon={User}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                isActive={isActiveRoute('/signin')}
              >
                Log In
              </MobileNavButton>
            </div>
          )}
        </div>
      </div>
    </nav>
    </>
  );
};

export default NavigationBar;
